import type {
  AreaOfOperations,
  CivilianZone,
  Definition,
  Force,
  ForceRole,
  IntelNote,
  MapMarker,
  Objective,
  PlacedComposition,
  RestrictedZone,
  Shape,
  Twist,
} from '../api/missions';
import { spokenPlace } from './scriptWords';

/**
 * What the designer does to a definition, as plain functions.
 *
 * The map component draws and the panels edit; the rules about what a new
 * element looks like, what an id may be and where a shape reaches live here, so
 * they can be proved by a unit test without a browser (designModel.test.ts) and
 * so the map stays a view.
 */

/** Everything the designer can have selected: on the map, or in a panel of its own. */
export type SelectionKind =
  | 'objective'
  | 'marker'
  | 'force'
  | 'intel'
  | 'civilian'
  | 'area'
  | 'zone'
  | 'start'
  | 'composition'
  /** A twist has no place on the map; it is selected from the Girs panel. */
  | 'twist'
  /**
   * A place the generator answered (release D, D11.8): a script's place or one
   * of the points (`point:base`, `point:extraction`). Never dragged: it moves
   * only among its kind's candidates, by «Mou».
   */
  | 'place';

export interface Selection {
  kind: SelectionKind;
  id: string;
}

/** What the pointer does on the next click. */
export type Tool =
  | 'select'
  | 'marker'
  | 'patrol'
  | 'garrison'
  | 'guard'
  | 'static'
  | 'vehicle'
  | 'civilian'
  | 'intel'
  | 'area'
  | 'zone'
  | 'route'
  | 'composition'
  /** Not in the palette: the next click says what a guard or a static weapon faces. */
  | 'watch';

/** The layers the map draws, in the order they stack. */
export const LAYERS = [
  'area',
  'zones',
  'markers',
  'objectives',
  'forces',
  'compositions',
  'civilians',
  'intel',
  'places',
] as const;
export type Layer = (typeof LAYERS)[number];

export type LayerState = Record<Layer, boolean>;

export const ALL_LAYERS: LayerState = {
  area: true,
  zones: true,
  markers: true,
  objectives: true,
  forces: true,
  compositions: true,
  civilians: true,
  intel: true,
  // Off until asked for: the generator's own records are the designer's
  // working notes, not the mission, and on a village cut into sectors they
  // stood on every sector's letter (the owner, 2026-10-11).
  places: false,
};

/**
 * An id the schema accepts (`^[a-z][a-z0-9_]{1,31}$`) that nothing else in the
 * definition uses. Derived from what the editor typed so a definition read by
 * hand still makes sense, with a number after it when the name is taken.
 */
export function nextId(prefix: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  const base =
    (prefix
      .toLowerCase()
      .normalize('NFD')
      .replace(/[^a-z0-9_]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .replace(/^[^a-z]+/, '')
      .slice(0, 24) || 'item') + '_';
  for (let n = 1; n < 1000; n++) {
    const candidate = `${base}${n}`;
    if (!used.has(candidate)) return candidate;
  }
  return `${base}${Date.now() % 100000}`;
}

/** Every id the definition already uses, so a new one never collides. */
export function usedIds(def: Definition): string[] {
  return [
    ...def.objectives.map((o) => o.id),
    ...(def.markers ?? []).map((m) => m.id),
    ...(def.forces ?? []).map((f) => f.id),
    ...(def.compositions ?? []).map((c) => c.id),
    ...(def.intel ?? []).map((i) => i.id),
    ...(def.twists ?? []).map((t) => t.id),
    ...(def.civilians?.zones ?? []).map((z) => z.id ?? ''),
    ...(def.area?.restricted ?? []).map((z) => z.id ?? ''),
  ].filter(Boolean);
}

/** The terrain's side in metres: the probe's number, or the commonest size when it is not known. */
export const DEFAULT_WORLD_SIZE = 20480;

export function worldSizeOf(def: Definition | null | undefined): number {
  const size = def?.terrain?.worldSize;
  return typeof size === 'number' && size > 0 ? size : DEFAULT_WORLD_SIZE;
}

// ---------------------------------------------------------------- new elements

export function newMarker(def: Definition, x: number, y: number, symbol = 'o_inf'): MapMarker {
  return {
    id: nextId(symbol, usedIds(def)),
    kind: 'icon',
    symbol,
    position: [x, y],
    text: '',
    colour: undefined,
    audience: 'players',
    revealAt: 'start',
  };
}

/**
 * A force with the defaults its role wants; the panel changes the rest.
 *
 * Everything an editor drops is quiet and stands as the engine sees fit — SAFE
 * and AUTO. A garrison used to come out AWARE and forced UP, which is a village
 * of men at windows staring down the road before anybody has been seen: it read
 * as an alerted position in every mission that used one, and undoing it by hand
 * on each element was the first thing an editor had to do.
 */
export function newForce(def: Definition, role: ForceRole, x: number, y: number): Force {
  const base: Force = {
    id: nextId(role, usedIds(def)),
    role,
    label: '',
    side: role === 'civilian' ? 'civilian' : 'east',
    category: 'Infantry',
    position: [x, y],
    activation: 'onApproach',
    behaviour: 'SAFE',
    stance: 'AUTO',
  };
  switch (role) {
    case 'patrol':
      return {
        ...base,
        radius: 400,
        waypoints: [],
        cycle: true,
        speed: 'LIMITED',
      };
    case 'garrison':
      return { ...base, radius: 120, size: 6 };
    case 'guard':
      return { ...base, radius: 40, size: 4, watch: [x, y + 200] };
    case 'static':
      return { ...base, count: 1, watch: [x, y + 300], category: 'Support' };
    case 'vehicle':
      return { ...base, category: 'Motorized', radius: 300, waypoints: [], cycle: true };
    default:
      return { ...base, radius: 150, size: 3, behaviour: 'CARELESS' };
  }
}

/**
 * A composition dropped on the map. It carries no objects yet: they come from
 * the preset the editor chooses in the panel, and the platform writes them into
 * the mission when it is published.
 */
export function newComposition(def: Definition, x: number, y: number): PlacedComposition {
  return {
    id: nextId('composition', usedIds(def)),
    label: '',
    position: [x, y],
    azimuth: 0,
    anchor: 'none',
    side: 'west',
    // Staff by default: a checkpoint the enemy built is not something the
    // players are handed on their map, and a base of our own is a deliberate
    // choice rather than the default.
    audience: 'staff',
    flatten: true,
  };
}

export function newIntel(def: Definition, x: number, y: number): IntelNote {
  return {
    id: nextId('intel', usedIds(def)),
    title: '',
    body: '',
    position: [x, y],
    confidence: 'probable',
    source: '',
    revealAt: 'briefing',
  };
}

export function newCivilianZone(def: Definition, x: number, y: number): CivilianZone {
  return {
    id: nextId('civils', usedIds(def)),
    label: '',
    position: [x, y],
    radius: 250,
    density: 0.4,
    panic: true,
    vehicles: 0.15,
  };
}

export function newTwist(def: Definition): Twist {
  return {
    id: nextId('gir', usedIds(def)),
    kind: 'orders',
    title: '',
    body: '',
    radio: [],
    at: 'start',
  };
}

export function newRestrictedZone(def: Definition, x: number, y: number): RestrictedZone {
  return {
    id: nextId('zona', usedIds(def)),
    shape: 'rect',
    center: [x, y],
    a: 400,
    b: 400,
    angle: 0,
    kind: 'nogo',
    label: '',
  };
}

export function newArea(x: number, y: number, a = 1500, b = 1500): AreaOfOperations {
  return {
    shape: 'rect',
    center: [x, y],
    a,
    b,
    angle: 0,
    label: 'Àrea d’operacions',
    colour: 'ColorBlack',
    showToPlayers: true,
    restricted: [],
  };
}

// ---------------------------------------------------------------- geometry

/** The centre of a shape, whatever kind it is. */
export function shapeCenter(shape: Shape): [number, number] {
  if (shape.shape === 'polygon') {
    const points = shape.points ?? [];
    if (points.length === 0) return [0, 0];
    const sx = points.reduce((n, p) => n + p[0], 0) / points.length;
    const sy = points.reduce((n, p) => n + p[1], 0) / points.length;
    return [sx, sy];
  }
  const c = shape.center ?? [0, 0];
  return [c[0], c[1]];
}

/** The box a shape needs on screen: [minX, minY, maxX, maxY] in metres. */
export function shapeBounds(shape: Shape): [number, number, number, number] {
  if (shape.shape === 'polygon') {
    const points = shape.points ?? [];
    if (points.length === 0) return [0, 0, 0, 0];
    const xs = points.map((p) => p[0]);
    const ys = points.map((p) => p[1]);
    return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
  }
  const [cx, cy] = shapeCenter(shape);
  const a = shape.a ?? 0;
  const b = shape.b ?? 0;
  // A rotated box needs the extent of the rotation, not the box.
  const t = ((shape.angle ?? 0) * Math.PI) / 180;
  const ex = Math.abs(a * Math.cos(t)) + Math.abs(b * Math.sin(t));
  const ey = Math.abs(a * Math.sin(t)) + Math.abs(b * Math.cos(t));
  return [cx - ex, cy - ey, cx + ex, cy + ey];
}

/** Whether a point in metres is inside a shape; the runtime asks the same of the area. */
export function inShape(shape: Shape, x: number, y: number): boolean {
  if (shape.shape === 'polygon') {
    const points = shape.points ?? [];
    let inside = false;
    for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
      const [xi, yi] = points[i];
      const [xj, yj] = points[j];
      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  }
  const [cx, cy] = shapeCenter(shape);
  const t = (-(shape.angle ?? 0) * Math.PI) / 180;
  const dx = (x - cx) * Math.cos(t) - (y - cy) * Math.sin(t);
  const dy = (x - cx) * Math.sin(t) + (y - cy) * Math.cos(t);
  const a = shape.a ?? 1;
  const b = shape.b ?? 1;
  if (shape.shape === 'ellipse') return (dx * dx) / (a * a) + (dy * dy) / (b * b) <= 1;
  return Math.abs(dx) <= a && Math.abs(dy) <= b;
}

/** How far outside the area of operations an element sits, in metres; 0 when it is inside. */
export function outsideBy(area: AreaOfOperations | undefined, x: number, y: number): number {
  if (!area) return 0;
  if (inShape(area, x, y)) return 0;
  const [cx, cy] = shapeCenter(area);
  const [minX, minY, maxX, maxY] = shapeBounds(area);
  const nearestX = Math.min(maxX, Math.max(minX, x));
  const nearestY = Math.min(maxY, Math.max(minY, y));
  const d = Math.hypot(x - nearestX, y - nearestY);
  return d > 0 ? d : Math.hypot(x - cx, y - cy);
}

// ---------------------------------------------------------------- checks

export interface Check {
  level: 'error' | 'warning';
  text: string;
  select?: Selection;
}

/**
 * What the "Comprovació" panel lists, computed here so a test can hold it to
 * account. Errors are the ones that would waste a night; warnings are the ones
 * a maker usually wants to know about and sometimes means.
 */
/** Metres an enemy element may be outside the area of operations without a warning. */
const FORCE_OUTSIDE_TOLERANCE = 750;

export function checks(def: Definition): Check[] {
  const out: Check[] = [];
  const ids = new Set<string>();
  const dup = (kind: SelectionKind, id: string, what: string) => {
    if (ids.has(id))
      out.push({
        level: 'error',
        text: `${what}: l’identificador «${id}» està repetit`,
        select: { kind, id },
      });
    ids.add(id);
  };

  def.objectives.forEach((o) => dup('objective', o.id, 'Objectiu'));
  (def.markers ?? []).forEach((m) => dup('marker', m.id, 'Marcador'));
  (def.forces ?? []).forEach((f) => dup('force', f.id, 'Element'));
  (def.compositions ?? []).forEach((c) => dup('composition', c.id, 'Composició'));
  (def.intel ?? []).forEach((n) => dup('intel', n.id, 'Nota'));

  if (def.objectives.length === 0) {
    out.push({ level: 'error', text: 'La missió no té cap objectiu.' });
  }
  const objectiveIds = new Set(def.objectives.map((o) => o.id));
  def.objectives.forEach((o) => {
    (o.dependsOn ?? []).forEach((d) => {
      if (!objectiveIds.has(d)) {
        out.push({
          level: 'error',
          text: `«${o.title ?? o.id}» depèn de «${d}», que no existeix.`,
          select: { kind: 'objective', id: o.id },
        });
      }
    });
    if (o.priority === 'hidden' && (o.dependsOn ?? []).length === 0) {
      out.push({
        level: 'warning',
        text: `«${o.title ?? o.id}» és ocult i no depèn de res: ningú el descobrirà.`,
        select: { kind: 'objective', id: o.id },
      });
    }
  });

  const area = def.area;
  if (area) {
    const check = (
      kind: SelectionKind,
      id: string,
      name: string,
      p: number[] | undefined,
      tolerance = 0,
    ) => {
      if (!p || p.length < 2) return;
      const out_ = outsideBy(area, p[0], p[1]);
      if (out_ > tolerance) {
        out.push({
          level: 'warning',
          text: `${name} és ${Math.round(out_)} m fora de l’àrea d’operacions.`,
          select: { kind, id },
        });
      }
    };
    def.objectives.forEach((o) =>
      check('objective', o.id, `L’objectiu «${o.title ?? o.id}»`, o.position),
    );
    // An enemy element is allowed a little way outside the box: the area of
    // operations bounds the night the players are told about, and a defence has
    // depth — a screen forward of the position and a reserve behind it are meant
    // to be beyond it. Only something that is plainly somewhere else is worth a
    // warning, or every generated mission opens with a page of them.
    (def.forces ?? []).forEach((f) =>
      check('force', f.id, `L’element «${f.label || f.id}»`, f.position, FORCE_OUTSIDE_TOLERANCE),
    );
    check('start', 'start', 'El punt de partida', def.sides.players.startPosition);
  }

  (def.forces ?? []).forEach((f) => {
    if (f.role === 'patrol' && (f.waypoints ?? []).length === 1) {
      out.push({
        level: 'warning',
        text: `La patrulla «${f.label || f.id}» té una sola parada: no és una ruta.`,
        select: { kind: 'force', id: f.id },
      });
    }
    if (f.activation?.startsWith('onObjective:')) {
      const target = f.activation.slice('onObjective:'.length);
      if (!objectiveIds.has(target)) {
        out.push({
          level: 'error',
          text: `«${f.label || f.id}» espera l’objectiu «${target}», que no existeix.`,
          select: { kind: 'force', id: f.id },
        });
      }
    }
  });

  const markerIds = new Set((def.markers ?? []).map((m) => m.id));
  (def.intel ?? []).forEach((n) => {
    if (!n.title.trim()) {
      out.push({
        level: 'error',
        text: 'Hi ha una nota d’intel sense títol.',
        select: { kind: 'intel', id: n.id },
      });
    }
    if (n.markerId && !markerIds.has(n.markerId)) {
      out.push({
        level: 'warning',
        text: `La nota «${n.title || n.id}» apunta a un marcador que no existeix.`,
        select: { kind: 'intel', id: n.id },
      });
    }
  });

  const intelIds = new Set((def.intel ?? []).map((n) => n.id));
  (def.twists ?? []).forEach((t) => {
    if (!t.title.trim()) {
      out.push({
        level: 'error',
        text: 'Hi ha un gir sense títol.',
        select: { kind: 'twist', id: t.id },
      });
    }
    const at = t.at ?? 'start';
    if (at.startsWith('onObjective:') && !objectiveIds.has(at.slice('onObjective:'.length))) {
      out.push({
        level: 'error',
        text: `El gir «${t.title || t.id}» espera un objectiu que no existeix.`,
        select: { kind: 'twist', id: t.id },
      });
    }
    if (at.startsWith('onIntel:') && !intelIds.has(at.slice('onIntel:'.length))) {
      out.push({
        level: 'error',
        text: `El gir «${t.title || t.id}» espera una nota que no existeix.`,
        select: { kind: 'twist', id: t.id },
      });
    }
    if (!t.body?.trim() && (t.radio ?? []).length === 0) {
      out.push({
        level: 'warning',
        text: `El gir «${t.title || t.id}» no diu res als jugadors.`,
        select: { kind: 'twist', id: t.id },
      });
    }
  });

  // A composition that names no preset builds nothing at all: the objects are
  // written into the mission from the preset when it is published, and there is
  // nothing to write.
  (def.compositions ?? []).forEach((c) => {
    if (!c.presetId && !(c.objects ?? []).length) {
      out.push({
        level: 'error',
        text: `La composició «${c.label || c.id}» no diu de què és feta: no s’hi construirà res.`,
        select: { kind: 'composition', id: c.id },
      });
    }
  });

  const enemyForces = (def.forces ?? []).filter((f) => f.role !== 'civilian');
  const groups = enemyForces.reduce((n, f) => n + (f.count ?? 1), 0);
  const budget = (def.settings?.budget as { maxActiveGroups?: number } | undefined)
    ?.maxActiveGroups;
  if (budget && groups > budget) {
    out.push({
      level: 'warning',
      text: `Hi ha ${groups} elements col·locats i el pressupost del servidor és de ${budget} grups actius.`,
    });
  }
  if (!def.briefing || Object.values(def.briefing).every((v) => !v?.trim())) {
    out.push({ level: 'warning', text: 'La missió no té ordre d’operació escrita.' });
  }

  // --- the placement rules a browser can check -------------------------------
  const extraction = def.points?.extraction?.position;
  if (area && extraction && extraction.length >= 2) {
    const clear = outsideBy(area, extraction[0], extraction[1]);
    if (clear <= 0) {
      out.push({ level: 'error', text: 'La zona d’extracció és dins l’àrea d’operacions.' });
    } else {
      const want = def.points?.extraction?.intent?.minDistance;
      if (want && clear < want * 0.8) {
        out.push({
          level: 'warning',
          text: `La zona d’extracció és a ${Math.round(clear)} m de la vora de l’àrea i el mitjà en demana ${want}.`,
        });
      }
    }
  }
  const insertion = def.points?.insertion?.position;
  const startPos = def.sides.players.startPosition;
  if (insertion && startPos && insertion.length >= 2 && startPos.length >= 2) {
    if (insertion[0] !== startPos[0] || insertion[1] !== startPos[1]) {
      out.push({
        level: 'warning',
        text: 'El punt d’inserció i la posició de partida dels jugadors no coincideixen.',
        select: { kind: 'start', id: 'start' },
      });
    }
  }

  // --- what an editor notices and a generator does not -----------------------
  (def.forces ?? []).forEach((f) => {
    if ((f.role === 'static' || f.role === 'guard') && (f.watch ?? []).length < 2) {
      out.push({
        level: 'warning',
        text: `«${f.label || f.id}» no mira enlloc: quedarà encarat cap on el motor el creï.`,
        select: { kind: 'force', id: f.id },
      });
    }
  });

  const placed = (def.markers ?? []).filter((m) => (m.position ?? []).length >= 2);
  placed.forEach((m, i) => {
    const a2 = m.position as number[];
    const other = placed.slice(i + 1).find((n) => {
      const b2 = n.position as number[];
      return Math.hypot(a2[0] - b2[0], a2[1] - b2[1]) < 25;
    });
    if (other) {
      out.push({
        level: 'warning',
        text: `Els marcadors «${m.text || m.id}» i «${other.text || other.id}» estan un damunt de l’altre.`,
        select: { kind: 'marker', id: m.id },
      });
    }
  });

  (def.intel ?? []).forEach((n) => {
    (n.unlocks ?? []).forEach((id) => {
      const target = def.objectives.find((o) => o.id === id);
      if (target && target.priority !== 'hidden') {
        out.push({
          level: 'warning',
          text: `La nota «${n.title || n.id}» obre «${target.title ?? id}», que ja es veu des del principi.`,
          select: { kind: 'intel', id: n.id },
        });
      }
    });
  });

  const limit = def.time?.limitMinutes ?? 0;
  if (limit > 0) {
    (def.twists ?? []).forEach((t) => {
      const fires = /^T\+(\d+)m$/.exec(t.at ?? '');
      if (fires && Number(fires[1]) > limit) {
        out.push({
          level: 'warning',
          text: `El gir «${t.title || t.id}» dispara al minut ${fires[1]} i la nit en dura ${limit}.`,
          select: { kind: 'twist', id: t.id },
        });
      }
    });
  }

  def.objectives.forEach((o) => {
    if (o.onFailure === 'endMission') {
      out.push({
        level: 'warning',
        text: `Si «${o.title ?? o.id}» falla, la nit s’acaba a l’instant.`,
        select: { kind: 'objective', id: o.id },
      });
    }
    const reveal = /^reveal:(.+)$/.exec(o.onFailure ?? '');
    if (reveal && !def.objectives.some((x) => x.id === reveal[1])) {
      out.push({
        level: 'error',
        text: `«${o.title ?? o.id}» obre «${reveal[1]}» en fallar, i aquest objectiu no existeix.`,
        select: { kind: 'objective', id: o.id },
      });
    }
  });
  return out;
}

/** The objective a title belongs to, for the panels' dependency pickers. */
export function objectiveLabel(o: Objective): string {
  return o.priority === 'hidden' ? `? (${o.id})` : (o.title ?? o.id);
}

/**
 * One place the generator answered, as the map draws it (release D, D11.8):
 * the base, the insertion when it is not the base, the way out when it is not
 * the base, and every place of the script. `movable` is false for what moves
 * only with something else: an alternative (it follows its parent) and the
 * insertion (it is the base's own point on a land start).
 */
export interface MapPlace {
  id: string;
  kind: 'base' | 'insertion' | 'extraction' | 'place';
  label: string;
  position: [number, number];
  movable: boolean;
}

/** The places and points of a definition, in the order the map draws them. */
export function placesOf(
  def: Definition,
  words: (p: NonNullable<NonNullable<Definition['script']>['places']>[number]) => string,
): MapPlace[] {
  const out: MapPlace[] = [];
  const at = (p?: number[]): [number, number] | null => (p && p.length >= 2 ? [p[0], p[1]] : null);
  const base = at(def.points?.base?.position);
  if (base) {
    out.push({
      id: 'point:base',
      kind: 'base',
      // «Base (military)» is a map label, not a name: said in Catalan.
      label: spokenPlace(def.points?.base?.label) || 'Base',
      position: base,
      movable: true,
    });
  }
  const insertion = at(def.points?.insertion?.position);
  if (insertion && (!base || insertion[0] !== base[0] || insertion[1] !== base[1])) {
    out.push({
      id: 'point:insertion',
      kind: 'insertion',
      label: def.points?.insertion?.label || 'Inserció',
      position: insertion,
      movable: false,
    });
  }
  const extraction = at(def.points?.extraction?.position);
  if (extraction && !def.points?.extraction?.atBase) {
    out.push({
      id: 'point:extraction',
      kind: 'extraction',
      label: def.points?.extraction?.label || 'Extracció',
      position: extraction,
      movable: true,
    });
  }
  for (const p of def.script?.places ?? []) {
    const position = at(p.position);
    if (!position) continue;
    out.push({
      id: p.id,
      kind: 'place',
      label: words(p),
      position,
      movable: !p.alternativeOf,
    });
  }
  return out;
}

/**
 * Whether anybody comes back, and how: `settings.respawn` as the designer edits
 * it, one of the owner's five ways (2026-10-30) or "the server's default".
 *
 *  - `server`: whatever the server the night is played on says (none until
 *    somebody changes it). A mission that never chose is this.
 *  - `none`: the dead are spectators.
 *  - `base`: back at the start base after `delay` seconds.
 *  - `leader`: back at the base, then beside the squad leader.
 *  - `lives`: `lives` each, or for the whole side when `shared`; out of them,
 *    a spectator.
 *  - `waves`: the dead come back together every `waveMinutes` minutes.
 *
 * The definition may carry the old bare word or the first object (`enabled`,
 * `wave`, `limit`, `position`); this reads them as the platform does
 * (MissionFiles.Respawn.of) and writes only the new object.
 */
export type RespawnMode = 'server' | 'none' | 'base' | 'leader' | 'lives' | 'waves';

export const RESPAWN_MODES: RespawnMode[] = ['server', 'none', 'base', 'leader', 'lives', 'waves'];

export interface RespawnModel {
  mode: RespawnMode;
  delay: number;
  lives: number;
  shared: boolean;
  waveMinutes: number;
}

const clampInt = (n: unknown, min: number, max: number, fallback: number): number =>
  typeof n === 'number' && Number.isFinite(n)
    ? Math.max(min, Math.min(max, Math.round(n)))
    : fallback;

export function respawnOf(settings: Record<string, unknown> | undefined): RespawnModel {
  const raw = settings?.respawn;
  const model: RespawnModel = {
    mode: 'server',
    delay: 10,
    lives: 3,
    shared: false,
    waveMinutes: 5,
  };
  if (typeof raw === 'string') {
    // The platform's reading of the bare word (MissionFiles.Respawn.of).
    const word = raw.toLowerCase();
    if (word === 'server') return model;
    model.mode = word === 'none' ? 'none' : word === 'wave' ? 'waves' : 'base';
    if (word === 'wave') model.waveMinutes = 2;
    return model;
  }
  if (!raw || typeof raw !== 'object') return model;
  const r = raw as {
    mode?: unknown;
    enabled?: unknown;
    delay?: unknown;
    lives?: unknown;
    shared?: unknown;
    waveMinutes?: unknown;
    wave?: unknown;
    limit?: { scope?: unknown; count?: unknown };
    position?: { kind?: unknown };
  };
  // The first object's wave was a delay: two minutes when it did not say.
  const oldWave = r.wave === true;
  model.delay = clampInt(r.delay, 0, 900, oldWave ? 120 : 10);
  model.lives = clampInt(r.lives ?? r.limit?.count, 1, 99, 3);
  model.shared = typeof r.shared === 'boolean' ? r.shared : r.limit?.scope === 'global';
  model.waveMinutes = clampInt(
    r.waveMinutes,
    1,
    30,
    oldWave ? Math.max(1, Math.ceil(model.delay / 60)) : 5,
  );
  if (typeof r.mode === 'string' && r.mode !== '') {
    model.mode = (RESPAWN_MODES as string[]).includes(r.mode) ? (r.mode as RespawnMode) : 'none';
    return model;
  }
  // The first object: not enabled is the generator's own default, which never chose.
  if (r.enabled !== true) return model;
  if (r.wave === true) model.mode = 'waves';
  else if (r.limit?.scope === 'perPlayer' || r.limit?.scope === 'global') model.mode = 'lives';
  else if (r.position?.kind === 'leader') model.mode = 'leader';
  else model.mode = 'base';
  return model;
}

/** The model back as the schema's object: only the numbers its mode uses. */
export function respawnNode(m: RespawnModel): Record<string, unknown> {
  const delay = clampInt(m.delay, 0, 900, 10);
  switch (m.mode) {
    case 'base':
    case 'leader':
      return { mode: m.mode, delay };
    case 'lives':
      return { mode: 'lives', delay, lives: clampInt(m.lives, 1, 99, 3), shared: m.shared };
    case 'waves':
      return { mode: 'waves', waveMinutes: clampInt(m.waveMinutes, 1, 30, 5) };
    default:
      return { mode: m.mode };
  }
}
